import { useEffect, useState } from "react";
import { Container, Spinner, Card, Row, Col, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { usePage } from "../../hooks/usePage";
import { usePrecargaEvents } from "../../hooks/usePrecargaEvents";
import { legacyAdminGet } from "../../utils/PrecargaApi";

export default function PrecargasCardLinkDetail() {
  usePage({ title: "Precargas - Detalle de Vinculacion" });

  const { eventid, linkId } = useParams();
  const navigate = useNavigate();
  const cashlessEventId = eventid ? parseInt(eventid) : null;

  const { precargaEvent, isLoadingPrecargaEvent } = usePrecargaEvents({
    cashlessEventId,
  });

  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (precargaEvent?.id && linkId) {
      setIsLoading(true);
      legacyAdminGet(`${precargaEvent.id}/card-links/${linkId}/detail`)
        .then((data) => {
          setDetail(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching card link detail:", error);
          setIsLoading(false);
        });
    }
  }, [precargaEvent?.id, linkId]);

  if (isLoadingPrecargaEvent || isLoading) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-3">Cargando detalle de vinculacion...</p>
        </div>
      </Container>
    );
  }

  if (!detail) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <p>No se encontro la vinculacion</p>
          <Button onClick={() => navigate(`/${eventid}/preloads/card-links`)}>
            Volver a Vinculaciones
          </Button>
        </div>
      </Container>
    );
  }

  // Supports both response shapes:
  // 1) { cardLink, user, wallet }
  // 2) legacy single object with cardLink fields
  const cardLink = detail.cardLink || detail;
  const user =
    detail.user ||
    (detail.cardLink?.user?.id ? detail.cardLink.user : null) ||
    cardLink?.user ||
    null;
  const wallet = detail.wallet || null;

  const formatDate = (value) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleString();
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `$${value}`;
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Button
            variant="secondary"
            onClick={() => navigate(`/${eventid}/preloads/card-links`)}
          >
            {"<- Volver a Vinculaciones"}
          </Button>
        </Col>
      </Row>

      <h2>Detalle de Vinculacion de Tarjeta</h2>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Informacion de la Vinculacion</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>ID:</strong> {cardLink.id}
              </p>
              <p>
                <strong>Link ID:</strong> {cardLink.linkId || "N/A"}
              </p>
              <p>
                <strong>Estado:</strong> {cardLink.status || "N/A"}
              </p>
              <p>
                <strong>Saldo al escanear:</strong>{" "}
                {formatMoney(cardLink.walletBalance)}
              </p>
              <p>
                <strong>Escaneado en:</strong> {formatDate(cardLink.scannedAt)}
              </p>
              {cardLink.failureReason && (
                <p>
                  <strong>Motivo de fallo:</strong> {cardLink.failureReason}
                </p>
              )}
              <p>
                <strong>Fecha de Creacion:</strong> {formatDate(cardLink.createdAt)}
              </p>
              <p>
                <strong>Ultima Actualizacion:</strong>{" "}
                {formatDate(cardLink.updatedAt)}
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Informacion del Usuario</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>ID:</strong> {user?.id || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {user?.email || "N/A"}
              </p>
              <p>
                <strong>Telefono:</strong> {user?.phone || "N/A"}
              </p>
              <p>
                <strong>Email Verificado:</strong> {user?.emailVerified ? "Si" : "No"}
              </p>
              <p>
                <strong>Telefono Verificado:</strong>{" "}
                {user?.phoneVerified ? "Si" : "No"}
              </p>
              {user?.id && (
                <Button
                  variant="link"
                  onClick={() =>
                    navigate(`/${eventid}/preloads/participants/${user.id}`)
                  }
                >
                  Ver Participante
                </Button>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>
              <strong>Billetera</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Wallet ID:</strong> {wallet?.id || cardLink.walletId || "N/A"}
              </p>
              {wallet && (
                <>
                  <p>
                    <strong>Saldo en Cuenta:</strong>{" "}
                    {formatMoney(wallet.balanceAccount)}
                  </p>
                  <p>
                    <strong>Saldo en Tarjeta:</strong> {formatMoney(wallet.balanceCard)}
                  </p>
                  <p>
                    <strong>Saldo Total:</strong> {formatMoney(wallet.totalBalance)}
                  </p>
                  <p>
                    <strong>Estado de Tarjeta:</strong> {wallet.cardStatus || "N/A"}
                  </p>
                </>
              )}
              <Button
                variant="link"
                onClick={() =>
                  navigate(`/${eventid}/preloads/wallets/${wallet?.id || cardLink.walletId}`)
                }
                disabled={!wallet?.id && !cardLink.walletId}
              >
                Ver Billetera
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
